import { useMemo, useState } from "react";
import type { TradeToEarnCampaign, TradeToEarnCampaignEpoch } from "@cede/types";
import type { EpochSelectorItem } from "@/components/shared/EpochSelector";
import { useMyLeaderboardEpochs } from "@/hooks/campaign/useMyLeaderboardEpochs";
import { useHubUser } from "@/hooks/user/useHubUser";
import { create } from "zustand";

/**
 * Gets the display label for an epoch.
 * Uses the epoch's custom name if available, otherwise returns a default "Epoch #N" label.
 *
 * @param epoch - The campaign epoch
 * @param index - Zero-based index of the epoch in the campaign
 * @returns Display label for the epoch
 * @internal
 */
const getEpochLabel = (epoch: TradeToEarnCampaignEpoch, index: number) => {
  const maybeNamedEpoch = epoch as TradeToEarnCampaignEpoch & { name?: string | null };
  if (maybeNamedEpoch?.name?.trim()) {
    return maybeNamedEpoch.name;
  }
  return `Epoch #${index + 1}`;
};

/**
 * Transforms campaign epochs into selector items with computed status and metadata.
 * Determines if each epoch is pending, active, or completed based on current time.
 *
 * @param epochs - Array of campaign epochs to transform (should be pre-filtered to exclude fully paused epochs)
 * @param pausedActiveEpochIds - Set of activeEpochIds that have fully paused siblings
 * @returns Array of epoch selector items with status, labels, and date information
 * @internal
 */
const buildEpochSelectorItems = (
  epochs: TradeToEarnCampaignEpoch[] | undefined,
  pausedActiveEpochIds: Set<number>,
): EpochSelectorItem[] => {
  if (!epochs?.length) {
    return [];
  }

  const now = new Date();

  return epochs.map((epoch, index) => {
    let status: NonNullable<EpochSelectorItem["status"]> = "pending";
    if (epoch.isPaused) {
      status = "paused";
    } else if (now >= epoch.start && now <= epoch.end) {
      status = "active";
    } else if (now > epoch.end) {
      status = "completed";
    }

    return {
      id: epoch.id,
      epochNumber: index,
      label: getEpochLabel(epoch, index),
      status,
      activeEpochId: epoch.activeEpochId,
      disabled: now < epoch.start,
      start: epoch.start,
      end: epoch.end,
      hadPausedPeriods: pausedActiveEpochIds.has(epoch.activeEpochId),
    };
  });
};

type CampaignEpochSelectionState = {
  selectedEpochId: number | null;
  setSelectedEpochId: (epochId: number | null) => void;
};

export const useCampaignEpochSelectionStore = create<CampaignEpochSelectionState>((set) => ({
  selectedEpochId: null,
  setSelectedEpochId: (epochId) => set({ selectedEpochId: epochId }),
}));

interface UseCampaignEpochSelectionOptions {
  campaign?: TradeToEarnCampaign | null;
}

/**
 * Provides epoch selection state and computed epoch data for a trading campaign.
 *
 * This hook:
 * - Filters out fully paused epochs from the selector
 * - Builds epoch selector items with status (pending/active/completed)
 * - Marks epochs that had paused periods with a warning flag
 * - Maintains selection state via Zustand store
 * - Owns the "Only my epochs" filter state, scoped to `campaign.name`
 *   (auth-aware disable rules + click handler that auto-switches the
 *   selected epoch to the user's most recent one when the filter flips on)
 *
 * Note: This hook does NOT handle auto-selection logic. Use `useAutoSelectEpoch`
 * alongside this hook for automatic epoch selection on campaign load/change.
 *
 * @param options - Configuration object
 * @param options.campaign - The trading campaign containing epochs (also supplies the slug for the "Only my epochs" data fetch via `campaign.name`)
 *
 * @returns Object containing:
 * - `epochItems` - Array of all epoch selector items with computed metadata
 * - `displayEpochs` - Epochs to render in the selector (filtered when the chip is on)
 * - `selectedEpoch` - The currently selected epoch data
 * - `selectedEpochItem` - The currently selected epoch selector item
 * - `selectEpoch` - Function to manually change the selected epoch by id
 * - `hasEpochs` - Boolean indicating if the campaign has any epochs
 * - `showOnlyUserEpochs` - Whether the "Only my epochs" filter chip is on
 * - `onToggleOnlyUserEpochs` - Handler to flip the filter chip
 * - `disableOnlyUserEpochsToggle` - True when the chip should be disabled (unauth, loading, or 0 records)
 */
export const useCampaignEpochSelection = ({ campaign }: UseCampaignEpochSelectionOptions) => {
  const { selectedEpochId, setSelectedEpochId } = useCampaignEpochSelectionStore();
  const { authenticated } = useHubUser();

  const pausedActiveEpochIds = useMemo(() => {
    const allEpochs = campaign?.epochs ?? [];
    return new Set(allEpochs.filter((e) => !!e.isFullyPaused).map((e) => e.activeEpochId));
  }, [campaign?.epochs]);

  const epochItems = useMemo(
    () => buildEpochSelectorItems(campaign?.epochs ?? [], pausedActiveEpochIds),
    [campaign?.epochs, pausedActiveEpochIds],
  );
  const hasEpochs = epochItems.length > 0;

  const selectedEpoch = useMemo(() => {
    if (selectedEpochId === null) return undefined;
    return campaign?.epochs?.find((epoch) => epoch.id === selectedEpochId);
  }, [selectedEpochId, campaign?.epochs]);

  const selectedEpochItem = useMemo(() => {
    if (selectedEpochId === null) return undefined;
    return epochItems.find((item) => item.id === selectedEpochId);
  }, [selectedEpochId, epochItems]);

  const [showOnlyUserEpochs, setShowOnlyUserEpochs] = useState(false);
  const {
    myEpochIdSet,
    isLoading: isMyEpochsLoading,
    hasAny: userHasAnyEpoch,
  } = useMyLeaderboardEpochs(campaign?.name);

  const isFilteringByMine = showOnlyUserEpochs && authenticated && userHasAnyEpoch;
  const displayEpochs = useMemo(
    () => (isFilteringByMine ? epochItems.filter((item) => myEpochIdSet.has(Number(item.id))) : epochItems),
    [isFilteringByMine, myEpochIdSet, epochItems],
  );

  // Auto-switch to the user's most recent epoch when the chip flips ON and
  // the current selection isn't one of theirs. Handler-driven so the swap
  // is a direct consequence of the click, not a derived effect.
  // TODO(mixpanel): once METRICS_EVENTS gains a value for this, fire
  // `TTE_LEADERBOARD_FILTER_MY_EPOCHS_TOGGLED` with { campaign, enabled } here.
  const onToggleOnlyUserEpochs = (next: boolean) => {
    if (next && userHasAnyEpoch) {
      const selectedIsMine = selectedEpoch?.id != null && myEpochIdSet.has(selectedEpoch.id);
      if (!selectedIsMine) {
        const userEpochs = epochItems.filter((item) => myEpochIdSet.has(Number(item.id)));
        const mostRecent = userEpochs.reduce<(typeof userEpochs)[number] | undefined>(
          (acc, item) => (acc == null || item.epochNumber > acc.epochNumber ? item : acc),
          undefined,
        );
        if (mostRecent?.id != null) setSelectedEpochId(Number(mostRecent.id));
      }
    }
    setShowOnlyUserEpochs(next);
  };
  const disableOnlyUserEpochsToggle = !authenticated || isMyEpochsLoading || !userHasAnyEpoch;

  return {
    epochItems,
    displayEpochs,
    selectedEpoch,
    selectedEpochItem,
    selectEpoch: setSelectedEpochId,
    hasEpochs,
    showOnlyUserEpochs,
    onToggleOnlyUserEpochs,
    disableOnlyUserEpochsToggle,
  };
};
